<template>
  <transition name="modal-fade">
    <div v-if="visible" class="forgot-modal-overlay" @click.self="handleClose">
      <div class="forgot-modal-container">
        <div class="forgot-header">
          <h2 class="forgot-title">{{ $t('forgot_password_modal.title') }}</h2>
          <button class="modal-close-btn" @click="handleClose" :aria-label="$t('common.close')">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="forgot-body">
          <div class="instruction-box">{{ $t('forgot_password_modal.instruction') }}</div>

          <!-- 找回方式切换 -->
          <div class="recovery-tabs">
            <button
              type="button"
              class="recovery-tab"
              :class="{ active: recoveryMethod === 'email' }"
              @click="switchMethod('email')"
            >
              {{ $t('forgot_password_modal.method_email') }}
            </button>
            <button
              type="button"
              class="recovery-tab"
              :class="{ active: recoveryMethod === 'phone' }"
              @click="switchMethod('phone')"
            >
              {{ $t('forgot_password_modal.method_phone') }}
            </button>
          </div>

          <form @submit.prevent="handleSubmit">
            <!-- 邮箱方式 -->
            <div v-if="recoveryMethod === 'email'" class="form-group">
              <input
                v-model="email"
                type="email"
                class="form-input"
                :placeholder="$t('forgot_password_modal.email_placeholder')"
              />
              <div class="captcha-row" style="margin-top: 12px;">
                <input
                  v-model="captcha"
                  type="text"
                  class="form-input captcha-input"
                  :placeholder="$t('forgot_password_modal.captcha_placeholder')"
                  maxlength="6"
                />
                <button
                  type="button"
                  class="btn-captcha"
                  :disabled="captchaCooldown > 0"
                  @click="sendCaptcha"
                >
                  {{ captchaCooldown > 0 ? $t('forgot_password_modal.resend_countdown', { seconds: captchaCooldown }) : $t('forgot_password_modal.send_captcha') }}
                </button>
              </div>
            </div>

            <!-- 手机方式 -->
            <div v-if="recoveryMethod === 'phone'" class="form-group">
              <div class="phone-row">
                <select v-model="phoneRegion" class="form-select phone-region-select">
                  <option value="+86">+86</option>
                </select>
                <input
                  v-model="phone"
                  type="tel"
                  class="form-input phone-input"
                  :placeholder="$t('forgot_password_modal.phone_placeholder')"
                />
              </div>
              <div class="captcha-row">
                <input
                  v-model="captcha"
                  type="text"
                  class="form-input captcha-input"
                  :placeholder="$t('forgot_password_modal.captcha_placeholder')"
                  maxlength="6"
                />
                <button
                  type="button"
                  class="btn-captcha"
                  :disabled="captchaCooldown > 0"
                  @click="sendCaptcha"
                >
                  {{ captchaCooldown > 0 ? $t('forgot_password_modal.resend_countdown', { seconds: captchaCooldown }) : $t('forgot_password_modal.send_captcha') }}
                </button>
              </div>
            </div>

            <!-- 新密码 -->
            <div class="password-row">
              <input
                v-model="newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input password-input"
                :placeholder="$t('forgot_password_modal.new_password_placeholder')"
              />
              <button
                type="button"
                class="btn-toggle-password"
                @click="showNewPassword = !showNewPassword"
                :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
              >
                <svg
                  v-if="!showNewPassword"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>

            <div class="password-row">
              <input
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input password-input"
                :placeholder="$t('forgot_password_modal.confirm_password_placeholder')"
              />
              <button
                type="button"
                class="btn-toggle-password"
                @click="showConfirmPassword = !showConfirmPassword"
                :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
              >
                <svg
                  v-if="!showConfirmPassword"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>

            <button type="submit" class="btn-confirm">{{ $t('forgot_password_modal.confirm') }}</button>
          </form>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions } from 'vuex'
import { userApi } from '@/api/user'

export default {
  name: 'ForgotPasswordModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      recoveryMethod: 'email',
      email: '',
      phoneRegion: '+86',
      phone: '',
      captcha: '',
      newPassword: '',
      confirmPassword: '',
      verificationCode: '',
      captchaCooldown: 0,
      captchaTimer: null,
      showNewPassword: false,
      showConfirmPassword: false
    }
  },
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        this.resetForm()
      } else {
        this.resetBodyOverflow()
      }
    }
  },
  beforeDestroy() {
    if (this.captchaTimer) {
      clearInterval(this.captchaTimer)
    }
  },
  methods: {
    ...mapActions(['closeForgotPasswordModal', 'openLoginModal', 'showToast']),
    handleClose() {
      this.closeForgotPasswordModal()
      this.openLoginModal()
    },
    switchMethod(method) {
      this.recoveryMethod = method
    },
    async sendCaptcha() {
      try {
        if (this.recoveryMethod === 'email') {
          if (!this.email) {
            this.showToast({ message: this.$t('forgot_password_modal.validation.email_required'), type: 'warning' })
            return
          }
          const res = await userApi.sendEmail({ email: this.email })
          if (res.code === 1) {
            this.verificationCode = res.data || ''
            this.showToast({ message: this.$t('forgot_password_modal.validation.captcha_sent'), type: 'success' })
          } else {
            this.showToast({ message: res.msg || '验证码发送失败', type: 'error' })
            return
          }
        } else {
          if (!this.phone) {
            this.showToast({ message: this.$t('forgot_password_modal.validation.phone_required'), type: 'warning' })
            return
          }
          const res = await userApi.sendCode({ phone: this.phone })
          if (res.code === 1) {
            this.verificationCode = res.data || ''
            this.showToast({ message: this.$t('forgot_password_modal.validation.captcha_sent'), type: 'success' })
          } else {
            this.showToast({ message: res.msg || '验证码发送失败', type: 'error' })
            return
          }
        }
      } catch {
        this.showToast({ message: '验证码发送失败，请稍后重试', type: 'error' })
        return
      }

      // 开始倒计时
      this.captchaCooldown = 60
      this.captchaTimer = setInterval(() => {
        this.captchaCooldown--
        if (this.captchaCooldown <= 0) {
          clearInterval(this.captchaTimer)
          this.captchaTimer = null
        }
      }, 1000)
    },
    async handleSubmit() {
      if (this.recoveryMethod === 'email') {
        if (!this.email) {
          this.showToast({ message: this.$t('forgot_password_modal.validation.email_required'), type: 'warning' })
          return
        }
      } else {
        if (!this.phone) {
          this.showToast({ message: this.$t('forgot_password_modal.validation.phone_required'), type: 'warning' })
          return
        }
      }
      if (!this.captcha) {
        this.showToast({ message: this.$t('forgot_password_modal.validation.captcha_required'), type: 'warning' })
        return
      }
      if (!this.newPassword) {
        this.showToast({ message: this.$t('forgot_password_modal.validation.password_required'), type: 'warning' })
        return
      }
      if (this.newPassword !== this.confirmPassword) {
        this.showToast({ message: this.$t('forgot_password_modal.validation.password_mismatch'), type: 'warning' })
        return
      }

      const params = {
        type: this.recoveryMethod === 'email' ? 1 : 2,
        yzm: this.captcha,
        userpwd: this.newPassword
      }
      if (this.recoveryMethod === 'email') {
        params.email = this.email
      } else {
        params.phone = this.phone
      }

      try {
        const res = await userApi.resetPwd(params)
        if (res.code === 1) {
          this.showToast({ message: this.$t('forgot_password_modal.validation.reset_success'), type: 'success' })
          this.resetForm()
          this.closeForgotPasswordModal()
          this.openLoginModal()
        } else {
          this.showToast({ message: res.msg || '重置密码失败', type: 'error' })
        }
      } catch {
        this.showToast({ message: '重置密码失败，请稍后重试', type: 'error' })
      }
    },
    resetForm() {
      this.recoveryMethod = 'email'
      this.email = ''
      this.phoneRegion = '+86'
      this.phone = ''
      this.captcha = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.verificationCode = ''
      if (this.captchaTimer) {
        clearInterval(this.captchaTimer)
        this.captchaTimer = null
      }
      this.captchaCooldown = 0
    },
    resetBodyOverflow() {
      const { cartModalVisible, loginModalVisible, registerModalVisible } = this.$store.state
      if (!cartModalVisible && !loginModalVisible && !registerModalVisible) {
        document.body.style.overflow = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.forgot-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
}

.forgot-modal-container {
  background: #fff;
  width: 440px;
  max-width: 94vw;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
}

.forgot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: #f0f0f0;
  border-bottom: 1px solid #e8e8e8;
}

.forgot-title {
  font-size: 18px;
  color: #333;
  font-weight: 500;
  margin: 0;
}

.modal-close-btn {
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  color: #bbb;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;

  &:hover {
    color: #666;
  }
}

.forgot-body {
  padding: 24px 28px 28px;
}

.instruction-box {
  background: #4a4a4a;
  color: #fff;
  font-size: 14px;
  padding: 12px 18px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
}

.recovery-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.recovery-tab {
  flex: 1;
  padding: 12px 16px;
  background: #fff;
  border: none;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;

  &:not(:last-child) {
    border-right: 1px solid #e0e0e0;
  }

  &.active {
    background: #8b7e55;
    color: #fff;
  }

  &:hover:not(.active) {
    background: #f5f5f5;
  }
}

.form-group {
  margin-bottom: 16px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.3s;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    border-color: #8b7e55;
  }
}

.phone-row {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.phone-region-select {
  width: 90px;
  flex-shrink: 0;
}

.phone-input {
  flex: 1;
}

.captcha-row {
  display: flex;
  gap: 10px;
}

.captcha-input {
  flex: 1;
}

.btn-captcha {
  width: 120px;
  padding: 12px 16px;
  background: #8b7e55;
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #7a6d4a;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.password-row {
  position: relative;
  display: flex;
  align-items: center;
  margin-top: 12px;
}

.password-input {
  flex: 1;
  padding-right: 48px;
}

.btn-toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  padding: 0;

  &:hover {
    color: #666;
  }

  &:focus {
    outline: none;
  }

  svg {
    display: block;
  }
}

.btn-confirm {
  width: 100%;
  padding: 14px;
  background: #4a4a4a;
  color: #fff;
  border: none;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
  letter-spacing: 1px;
  margin-top: 8px;

  &:hover {
    background: #3a3a3a;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .forgot-modal-container,
.modal-fade-leave-active .forgot-modal-container {
  transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .forgot-modal-container,
.modal-fade-leave-to .forgot-modal-container {
  transform: scale(0.96);
}

@media (max-width: 480px) {
  .forgot-modal-container {
    padding: 0;
  }

  .forgot-body {
    padding: 20px 20px 24px;
  }

  .phone-row {
    flex-direction: column;
  }

  .phone-region-select {
    width: 100%;
  }

  .captcha-row {
    flex-direction: column;
  }

  .btn-captcha {
    width: 100%;
  }
}
</style>
